import { useState } from 'react'
import { ClipboardDocumentIcon, KeyIcon } from '@heroicons/react/24/outline'
import classNames from '../../utils/classNames.js';

// Always the public endpoint: users configure their AI client against
// production, whatever REACT_APP_SERVER_URL this build uses.
const MCP_URL = 'https://api.extracta.ai/mcp';
const API_KEY_PLACEHOLDER = 'YOUR_API_KEY';

const buildClients = (mcpUrl) => [
  {
    name: 'Claude Code',
    hint: 'Run in your terminal:',
    code: `claude mcp add --transport http extracta ${mcpUrl} \\\n  --header "Authorization: Bearer ${API_KEY_PLACEHOLDER}"`,
  },
  {
    name: 'Cursor',
    hint: <>Add to <code className="font-mono">~/.cursor/mcp.json</code> (or <code className="font-mono">.cursor/mcp.json</code> in your project):</>,
    code: JSON.stringify({
      mcpServers: {
        extracta: {
          url: mcpUrl,
          headers: { Authorization: `Bearer ${API_KEY_PLACEHOLDER}` },
        },
      },
    }, null, 2),
  },
  {
    name: 'Codex',
    hint: <>Run in your terminal. Codex reads the key from the <code className="font-mono">EXTRACTA_API_KEY</code> environment variable, so add the <code className="font-mono">export</code> line to your shell profile (e.g. <code className="font-mono">~/.zshrc</code>) to keep it across sessions:</>,
    code: `export EXTRACTA_API_KEY="${API_KEY_PLACEHOLDER}"\ncodex mcp add extracta --url ${mcpUrl} \\\n  --bearer-token-env-var EXTRACTA_API_KEY`,
  },
  {
    name: 'Other AI tools',
    hint: <>Use these settings in any tool that supports remote MCP servers, such as Open WebUI, LibreChat, n8n or your own agents. Look for an option like <span className="font-medium">Add MCP server</span> or <span className="font-medium">External tools</span>:</>,
    fields: [
      { label: 'Server URL', value: mcpUrl },
      { label: 'Type / transport', value: 'Streamable HTTP', note: 'Sometimes just called "HTTP". Not SSE or stdio.' },
      { label: 'Authentication', value: 'Bearer token', note: 'Use your API key as the token.', copyable: false },
      { label: 'Or as a custom header', value: `Authorization: Bearer ${API_KEY_PLACEHOLDER}`, note: 'If the tool asks for a header instead of a token.' },
    ],
  },
];

const tools = [
  { group: 'Data extraction', name: 'list_extractions', description: 'List your extraction templates.' },
  { group: 'Data extraction', name: 'create_extraction', description: 'Create an extraction with the fields to extract.' },
  { group: 'Data extraction', name: 'get_extraction', description: 'View an extraction, its fields and batches.' },
  { group: 'Data extraction', name: 'update_extraction', description: 'Change the name, language, fields or options.' },
  { group: 'Data extraction', name: 'delete_extraction', description: 'Delete an extraction, a batch or a single file.' },
  { group: 'Data extraction', name: 'upload_files', description: 'Upload documents (URL or base64) to start processing.' },
  { group: 'Data extraction', name: 'get_batch_results', description: 'Get the extracted data for a batch or file.' },
  { group: 'Document classification', name: 'list_classifications', description: 'List your classification templates.' },
  { group: 'Document classification', name: 'create_classification', description: 'Create a classification with document types.' },
  { group: 'Document classification', name: 'get_classification', description: 'View a classification and its batches.' },
  { group: 'Document classification', name: 'update_classification', description: 'Replace the document types of a classification.' },
  { group: 'Document classification', name: 'delete_classification', description: 'Delete a classification, a batch or files.' },
  { group: 'Document classification', name: 'upload_classification_files', description: 'Upload documents to be classified.' },
  { group: 'Document classification', name: 'get_classification_results', description: 'Get the predicted document type for each file.' },
  { group: 'Account', name: 'get_credits', description: 'Check your remaining credits (used by both data extraction and classification).' },
];

const examplePrompts = [
  'Create an extraction for supplier invoices with invoice number, date, supplier name, line items and total.',
  'Upload https://example.com/invoice.pdf to my "Supplier invoices" extraction and show me the results.',
  'How many credits do I have left?',
];

function CodeBlock({ code, handleCopy }) {
  return (
    <div className="relative mt-2">
      <pre className="overflow-x-auto rounded-md bg-gray-900 p-4 pr-24 font-mono text-sm leading-6 text-gray-100">
        {code}
      </pre>
      <button
        type="button"
        onClick={() => handleCopy(code)}
        className="absolute right-2 top-2 inline-flex items-center gap-x-1 rounded-md bg-white/10 px-2 py-1 text-xs font-semibold text-white hover:bg-white/20"
      >
        <ClipboardDocumentIcon className="h-4 w-4" aria-hidden="true" />
        Copy
      </button>
    </div>
  )
}

function ConnectionFields({ fields, handleCopy }) {
  return (
    <dl className="mt-2 divide-y divide-gray-200 rounded-md ring-1 ring-inset ring-gray-300">
      {fields.map((field) => (
        <div key={field.label} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center">
          <dt className="w-48 flex-none text-sm font-medium text-gray-900">{field.label}</dt>
          <dd className="flex min-w-0 flex-1 items-center justify-between gap-4">
            <div className="min-w-0">
              <code className="break-all font-mono text-sm text-gray-900">{field.value}</code>
              {field.note && <p className="mt-0.5 text-xs text-gray-500">{field.note}</p>}
            </div>
            {field.copyable !== false && <button
              type="button"
              onClick={() => handleCopy(field.value)}
              className="inline-flex flex-none items-center gap-x-1 rounded-md bg-white px-2 py-1 text-xs font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            >
              <ClipboardDocumentIcon className="h-4 w-4" aria-hidden="true" />
              Copy
            </button>}
          </dd>
        </div>
      ))}
    </dl>
  )
}

export default function Mcp({ handleCopy, openApiKeys }) {
  const mcpUrl = MCP_URL;
  const clients = buildClients(mcpUrl);
  const [selectedClient, setSelectedClient] = useState(0);
  const client = clients[selectedClient];

  return (
    <div className="w-full space-y-10">
      <div>
        <h1 className="text-xl font-semibold leading-6 text-gray-900">MCP Server</h1>
        <p className="mt-2 text-sm text-gray-700">
          Connect AI assistants such as Claude, Cursor or Codex to your Extracta account through the
          Model Context Protocol (MCP). Your assistant can then create extractions, upload documents and
          read the extracted data for you, just by asking in plain language.
        </p>
      </div>

      <div>
        <h2 className="text-base font-semibold leading-6 text-gray-900">1. Server URL</h2>
        <div className="mt-3 flex items-center gap-2">
          <code className="block flex-1 min-w-0 truncate rounded-md bg-white px-3 py-2 font-mono text-sm text-gray-900 ring-1 ring-inset ring-gray-300">
            {mcpUrl}
          </code>
          <button
            type="button"
            onClick={() => handleCopy(mcpUrl)}
            className="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-2.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
          >
            <ClipboardDocumentIcon className="-mr-0.5 h-5 w-5" aria-hidden="true" />
            Copy
          </button>
        </div>
      </div>

      <div>
        <h2 className="text-base font-semibold leading-6 text-gray-900">2. Authentication</h2>
        <p className="mt-2 text-sm text-gray-700">
          The MCP server uses your regular API key, sent as <code className="font-mono">Authorization: Bearer &lt;api_key&gt;</code>.
          Replace <code className="font-mono">{API_KEY_PLACEHOLDER}</code> in the configuration below with one of your keys.
          Treat this key like a password: anyone who has it can use your credits.
        </p>
        <button
          type="button"
          onClick={openApiKeys}
          className="mt-3 inline-flex items-center gap-x-1.5 rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
        >
          <KeyIcon className="-ml-0.5 h-5 w-5 text-gray-400" aria-hidden="true" />
          Manage API keys
        </button>
      </div>

      <div>
        <h2 className="text-base font-semibold leading-6 text-gray-900">3. Add Extracta to your AI client</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {clients.map((item, index) => (
            <button
              key={item.name}
              type="button"
              onClick={() => setSelectedClient(index)}
              className={classNames(
                index === selectedClient
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50',
                'rounded-md px-3 py-1.5 text-sm font-medium'
              )}
            >
              {item.name}
            </button>
          ))}
        </div>
        <div className="mt-4">
          <p className="text-sm text-gray-700">{client.hint}</p>
          {client.fields
            ? <ConnectionFields fields={client.fields} handleCopy={handleCopy} />
            : <CodeBlock code={client.code} handleCopy={handleCopy} />}
        </div>
        <p className="mt-3 text-sm text-gray-500">
          Works with any client that supports remote MCP servers over Streamable HTTP with an API key or custom
          header. Clients that only support OAuth sign-in are not supported yet.
        </p>
      </div>

      <div>
        <h2 className="text-base font-semibold leading-6 text-gray-900">Try it</h2>
        <p className="mt-2 text-sm text-gray-700">Once connected, ask your assistant things like:</p>
        <ul className="mt-3 space-y-2">
          {examplePrompts.map((prompt) => (
            <li key={prompt} className="flex items-center justify-between gap-4 rounded-md bg-gray-100 py-2 pl-4 pr-2 text-sm text-gray-800">
              <span className="italic">“{prompt}”</span>
              <button
                type="button"
                onClick={() => handleCopy(prompt)}
                className="inline-flex flex-none items-center gap-x-1 rounded-md bg-white px-2 py-1 text-xs font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                <ClipboardDocumentIcon className="h-4 w-4" aria-hidden="true" />
                Copy
              </button>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-sm text-gray-500">
          Documents can be passed as a public URL or as file content. Uploads consume credits exactly like
          the API, and everything your assistant creates appears in your dashboard.
        </p>
      </div>

      <div>
        <h2 className="text-base font-semibold leading-6 text-gray-900">Available tools</h2>
        <div className="mt-3 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <table className="min-w-full divide-y divide-gray-300">
              <thead>
                <tr>
                  <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">TOOL</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">DESCRIPTION</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">PRODUCT</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {tools.map((tool) => (
                  <tr key={tool.name}>
                    <td className="whitespace-nowrap py-3 pl-4 pr-3 font-mono text-sm text-gray-900 sm:pl-0">{tool.name}</td>
                    <td className="px-3 py-3 text-sm text-gray-700">{tool.description}</td>
                    <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">{tool.group}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}
